import React, { useEffect, useState, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users, FileText, FolderTree, Bell, MessageSquare, Megaphone, Upload, Trash2, Loader2, Plus, Mail, CheckCheck, GraduationCap, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { SectionTitle } from "@/components/Section";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const refreshStats = useCallback(() => api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}), []);
  useEffect(() => { refreshStats(); }, [refreshStats]);

  const cards = [
    { label: "Students", value: stats?.students, icon: Users },
    { label: "Revision Kits", value: stats?.kits, icon: FileText },
    { label: "Subjects", value: stats?.subjects, icon: FolderTree },
    { label: "Messages", value: stats?.messages, icon: MessageSquare, badge: stats?.unread_messages },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <SectionTitle sub="Manage kits, subjects, notices, students and messages" testId="admin-title">Admin Dashboard</SectionTitle>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="gov-card flex items-center gap-3 p-4" data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-[#1F3A68]/10"><c.icon size={22} className="text-[#1F3A68]" /></div>
            <div>
              <div className="font-serif text-2xl font-bold text-[#0B2545]">{c.value ?? "—"}{c.badge ? <span className="ml-1 rounded-full bg-[#C4302B] px-1.5 py-0.5 align-top text-[10px] text-white">{c.badge} new</span> : null}</div>
              <div className="text-xs text-gray-500">{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="kits">
        <TabsList className="flex w-full flex-wrap justify-start gap-1 rounded-sm bg-white p-1">
          <TabsTrigger value="kits" data-testid="tab-kits">Kits</TabsTrigger>
          <TabsTrigger value="subjects" data-testid="tab-subjects">Subjects</TabsTrigger>
          <TabsTrigger value="announcements" data-testid="tab-announcements">Notices</TabsTrigger>
          <TabsTrigger value="ticker" data-testid="tab-ticker">Ticker</TabsTrigger>
          <TabsTrigger value="tests" data-testid="tab-tests">Tests</TabsTrigger>
          <TabsTrigger value="enrollments" data-testid="tab-enrollments">Enrolments</TabsTrigger>
          <TabsTrigger value="coursenotes" data-testid="tab-coursenotes">Course Notes</TabsTrigger>
          <TabsTrigger value="students" data-testid="tab-students">Students</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
        </TabsList>

        <TabsContent value="kits"><KitsTab onChange={refreshStats} /></TabsContent>
        <TabsContent value="subjects"><SubjectsTab onChange={refreshStats} /></TabsContent>
        <TabsContent value="announcements"><AnnouncementsTab /></TabsContent>
        <TabsContent value="ticker"><TickerTab /></TabsContent>
        <TabsContent value="tests"><TestsTab /></TabsContent>
        <TabsContent value="enrollments"><EnrollmentsTab /></TabsContent>
        <TabsContent value="coursenotes"><CourseNotesTab /></TabsContent>
        <TabsContent value="students"><StudentsTab /></TabsContent>
        <TabsContent value="messages"><MessagesTab onChange={refreshStats} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Panel({ title, children, action }) {
  return (
    <div className="gov-card mt-4 overflow-hidden">
      <div className="gov-card-header flex items-center justify-between px-4 py-2.5 font-serif text-base font-bold">{title}{action}</div>
      <div className="p-4">{children}</div>
    </div>
  );
}

const inputCls = "w-full rounded-sm border border-[#D1D5DB] px-3 py-2 text-sm focus:border-[#1F3A68] bg-white";

function KitsTab({ onChange }) {
  const [kits, setKits] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({ title: "", subject: "", description: "", external_link: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/kits?limit=500").then(({ data }) => setKits(data)).catch(() => {});
    api.get("/subjects").then(({ data }) => setSubjects(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject) { toast.error("Please select a subject"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("subject", form.subject);
      fd.append("description", form.description);
      fd.append("external_link", form.external_link);
      if (file) fd.append("file", file);
      await api.post("/kits", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Kit added");
      setForm({ title: "", subject: "", description: "", external_link: "" });
      setFile(null);
      e.target.reset?.();
      load(); onChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setLoading(false); }
  };

  const del = async (id) => {
    await api.delete(`/kits/${id}`);
    toast.success("Kit deleted");
    load(); onChange?.();
  };

  const replaceFile = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.put(`/kits/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded to kit");
      load(); onChange?.();
    } catch {
      toast.error("Upload failed");
    }
  };

  return (
    <>
      <Panel title="Add New Revision Kit">
        <form onSubmit={submit} className="space-y-3" data-testid="kit-form">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input required placeholder="Kit title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="kit-title" />
            <select required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} data-testid="kit-subject">
              <option value="">Select subject</option>
              {subjects.map((s) => <option key={s.id} value={s.slug}>{s.name}</option>)}
            </select>
          </div>
          <textarea placeholder="Short description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-none`} data-testid="kit-desc" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-600">Upload PDF (optional)</label>
              <input type="file" accept=".pdf,.doc,.docx,.zip" onChange={(e) => setFile(e.target.files[0])} className="w-full text-sm" data-testid="kit-file" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-600">Or external link (optional)</label>
              <input placeholder="https://..." value={form.external_link} onChange={(e) => setForm({ ...form, external_link: e.target.value })} className={inputCls} data-testid="kit-link" />
            </div>
          </div>
          <button type="submit" disabled={loading} className="flex items-center gap-2 rounded-sm bg-[#1F3A68] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B2545] disabled:opacity-60" data-testid="kit-submit">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Add Kit
          </button>
        </form>
      </Panel>

      <Panel title={`All Kits (${kits.length})`}>
        <div className="overflow-x-auto">
          <table className="zebra w-full border border-[#E5E7EB] text-sm" data-testid="admin-kits-table">
            <thead>
              <tr className="bg-[#1F3A68] text-left text-white">
                <th className="px-3 py-2">Title</th><th className="px-3 py-2">Subject</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Downloads</th><th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {kits.map((k) => (
                <tr key={k.id} className="border-t border-[#E5E7EB]">
                  <td className="px-3 py-2 font-medium text-[#0B2545]">{k.title}</td>
                  <td className="px-3 py-2">{k.subject}</td>
                  <td className="px-3 py-2">{k.storage_path ? "File" : k.external_link ? "Link" : <span className="text-[#D97706]">No file</span>}</td>
                  <td className="px-3 py-2">{k.download_count || 0}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-3">
                      <label className="flex cursor-pointer items-center gap-1 text-xs text-[#1F3A68] hover:underline" data-testid={`replace-kit-${k.id}`}>
                        <RefreshCw size={13} /> {k.storage_path ? "Replace" : "Upload"} PDF
                        <input type="file" accept=".pdf,.doc,.docx,.zip" className="hidden" onChange={(e) => e.target.files[0] && replaceFile(k.id, e.target.files[0])} />
                      </label>
                      <button onClick={() => del(k.id)} className="text-[#C4302B] hover:underline" data-testid={`del-kit-${k.id}`}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}

function SubjectsTab({ onChange }) {
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "", description: "" });
  const load = useCallback(() => api.get("/subjects").then(({ data }) => setSubjects(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/subjects", { ...form, slug: form.slug || form.name.toLowerCase().replace(/\s+/g, "-") });
      toast.success("Subject added");
      setForm({ name: "", slug: "", description: "" });
      load(); onChange?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/subjects/${id}`); toast.success("Deleted"); load(); onChange?.(); };

  return (
    <>
      <Panel title="Add Subject">
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="subject-form">
          <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="subject-name" />
          <input placeholder="Slug (optional)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={inputCls} data-testid="subject-slug" />
          <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} data-testid="subject-desc" />
          <button type="submit" className="flex items-center gap-1.5 rounded-sm bg-[#1F3A68] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B2545]" data-testid="subject-submit"><Plus size={15} /> Add</button>
        </form>
      </Panel>
      <Panel title={`Subjects (${subjects.length})`}>
        <ul className="divide-y divide-[#E5E7EB]" data-testid="admin-subjects-list">
          {subjects.map((s) => (
            <li key={s.id} className="flex items-center justify-between py-2.5 text-sm">
              <span><strong className="text-[#0B2545]">{s.name}</strong> <span className="text-gray-400">({s.slug}) · {s.kit_count} kits</span></span>
              <button onClick={() => del(s.id)} className="text-[#C4302B]" data-testid={`del-subject-${s.id}`}><Trash2 size={16} /></button>
            </li>
          ))}
        </ul>
      </Panel>
    </>
  );
}

function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", is_important: true });
  const load = useCallback(() => api.get("/announcements").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => { e.preventDefault(); await api.post("/announcements", form); toast.success("Notice posted"); setForm({ title: "", body: "", is_important: true }); load(); };
  const del = async (id) => { await api.delete(`/announcements/${id}`); toast.success("Deleted"); load(); };
  return (
    <>
      <Panel title="Post a Notice">
        <form onSubmit={submit} className="space-y-3" data-testid="ann-form">
          <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="ann-title" />
          <textarea required placeholder="Details" rows={2} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={`${inputCls} resize-none`} data-testid="ann-body" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_important} onChange={(e) => setForm({ ...form, is_important: e.target.checked })} data-testid="ann-important" /> Mark as important</label>
          <button type="submit" className="flex items-center gap-1.5 rounded-sm bg-[#1F3A68] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B2545]" data-testid="ann-submit"><Megaphone size={15} /> Post</button>
        </form>
      </Panel>
      <Panel title={`Notices (${items.length})`}>
        <ul className="divide-y divide-[#E5E7EB]" data-testid="admin-ann-list">
          {items.map((a) => (
            <li key={a.id} className="flex items-start justify-between gap-3 py-2.5 text-sm">
              <div><strong className="text-[#0B2545]">{a.title}</strong>{a.is_important && <span className="ml-2 rounded-sm bg-[#D97706]/10 px-1.5 text-[10px] font-bold uppercase text-[#D97706]">Important</span>}<p className="text-gray-600">{a.body}</p></div>
              <button onClick={() => del(a.id)} className="shrink-0 text-[#C4302B]" data-testid={`del-ann-${a.id}`}><Trash2 size={16} /></button>
            </li>
          ))}
        </ul>
      </Panel>
    </>
  );
}

function TickerTab() {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const load = useCallback(() => api.get("/ticker").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => { e.preventDefault(); await api.post("/ticker", { text, active: true }); toast.success("Added to ticker"); setText(""); load(); };
  const del = async (id) => { await api.delete(`/ticker/${id}`); toast.success("Removed"); load(); };
  return (
    <>
      <Panel title="Add 'What's New' Ticker Item">
        <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row" data-testid="ticker-form">
          <input required placeholder="e.g. 📚 New Polity kit added" value={text} onChange={(e) => setText(e.target.value)} className={inputCls} data-testid="ticker-text" />
          <button type="submit" className="flex shrink-0 items-center gap-1.5 rounded-sm bg-[#1F3A68] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B2545]" data-testid="ticker-submit"><Plus size={15} /> Add</button>
        </form>
      </Panel>
      <Panel title={`Ticker Items (${items.length})`}>
        <ul className="divide-y divide-[#E5E7EB]" data-testid="admin-ticker-list">
          {items.map((t) => (
            <li key={t.id} className="flex items-center justify-between py-2.5 text-sm">
              <span className="text-[#0B2545]">{t.text}</span>
              <button onClick={() => del(t.id)} className="text-[#C4302B]" data-testid={`del-ticker-${t.id}`}><Trash2 size={16} /></button>
            </li>
          ))}
        </ul>
      </Panel>
    </>
  );
}

function TestsTab() {
  const [tests, setTests] = useState([]);
  const [tForm, setTForm] = useState({ title: "", description: "", subject: "", duration_minutes: 6 });
  const [active, setActive] = useState(null);
  const [qForm, setQForm] = useState({ question_text: "", options: ["", "", "", ""], correct_index: 0, explanation: "" });
  const load = useCallback(() => api.get("/admin/tests").then(({ data }) => setTests(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const createTest = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/tests", { ...tForm, duration_minutes: Number(tForm.duration_minutes) || 6 });
    toast.success("Test created — now add questions");
    setTForm({ title: "", description: "", subject: "", duration_minutes: 6 });
    await load();
    setActive(data.id);
  };
  const delTest = async (id) => { await api.delete(`/tests/${id}`); toast.success("Test deleted"); if (active === id) setActive(null); load(); };
  const addQuestion = async (e) => {
    e.preventDefault();
    if (qForm.options.some((o) => !o.trim())) { toast.error("Fill all 4 options"); return; }
    await api.post("/questions", { test_id: active, ...qForm });
    toast.success("Question added");
    setQForm({ question_text: "", options: ["", "", "", ""], correct_index: 0, explanation: "" });
    load();
  };
  const delQuestion = async (qid) => { await api.delete(`/questions/${qid}`); toast.success("Question removed"); load(); };

  return (
    <>
      <Panel title="Create a Mock Test">
        <form onSubmit={createTest} className="space-y-3" data-testid="test-form">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input required placeholder="Test title" value={tForm.title} onChange={(e) => setTForm({ ...tForm, title: e.target.value })} className={inputCls} data-testid="test-title" />
            <input placeholder="Subject (optional)" value={tForm.subject} onChange={(e) => setTForm({ ...tForm, subject: e.target.value })} className={inputCls} data-testid="test-subject" />
          </div>
          <input placeholder="Short description" value={tForm.description} onChange={(e) => setTForm({ ...tForm, description: e.target.value })} className={inputCls} data-testid="test-desc" />
          <label className="flex items-center gap-2 text-sm text-gray-700">Duration (minutes):
            <input type="number" min="1" value={tForm.duration_minutes} onChange={(e) => setTForm({ ...tForm, duration_minutes: e.target.value })} className={`${inputCls} w-24`} data-testid="test-duration" />
          </label>
          <button type="submit" className="flex items-center gap-1.5 rounded-sm bg-[#1F3A68] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B2545]" data-testid="test-submit"><Plus size={15} /> Create Test</button>
        </form>
      </Panel>

      {tests.map((t) => (
        <Panel key={t.id} title={`${t.title} · ${t.questions.length} Q · +2/−0.66`} action={<button onClick={() => delTest(t.id)} className="text-white/80 hover:text-white" data-testid={`del-test-${t.id}`}><Trash2 size={16} /></button>}>
          <ul className="mb-3 space-y-2" data-testid={`test-questions-${t.id}`}>
            {t.questions.map((q, i) => (
              <li key={q.id} className="rounded-sm border border-[#E5E7EB] p-2.5 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[#0B2545]"><strong>{i + 1}.</strong> {q.question_text}</span>
                  <button onClick={() => delQuestion(q.id)} className="shrink-0 text-[#C4302B]" data-testid={`del-q-${q.id}`}><Trash2 size={14} /></button>
                </div>
                <div className="mt-1 text-xs text-gray-500">Correct: <strong className="text-[#16A34A]">{q.options[q.correct_index]}</strong></div>
              </li>
            ))}
            {t.questions.length === 0 && <li className="text-sm text-gray-400">No questions yet.</li>}
          </ul>
          {active === t.id ? (
            <form onSubmit={addQuestion} className="space-y-2 rounded-sm bg-gray-50 p-3" data-testid="question-form">
              <textarea required placeholder="Question text" rows={2} value={qForm.question_text} onChange={(e) => setQForm({ ...qForm, question_text: e.target.value })} className={`${inputCls} resize-none`} data-testid="q-text" />
              {qForm.options.map((opt, oi) => (
                <label key={oi} className="flex items-center gap-2">
                  <input type="radio" name="correct" checked={qForm.correct_index === oi} onChange={() => setQForm({ ...qForm, correct_index: oi })} className="accent-[#16A34A]" data-testid={`q-correct-${oi}`} />
                  <input required placeholder={`Option ${oi + 1}`} value={opt} onChange={(e) => setQForm((f) => { const o = [...f.options]; o[oi] = e.target.value; return { ...f, options: o }; })} className={inputCls} data-testid={`q-opt-${oi}`} />
                </label>
              ))}
              <input placeholder="Explanation (optional)" value={qForm.explanation} onChange={(e) => setQForm({ ...qForm, explanation: e.target.value })} className={inputCls} data-testid="q-explanation" />
              <div className="flex gap-2">
                <button type="submit" className="rounded-sm bg-[#1F3A68] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#0B2545]" data-testid="q-submit">Add Question</button>
                <button type="button" onClick={() => setActive(null)} className="rounded-sm border border-gray-300 px-3 py-1.5 text-sm">Done</button>
              </div>
              <p className="text-xs text-gray-500">Select the radio next to the correct option.</p>
            </form>
          ) : (
            <button onClick={() => setActive(t.id)} className="flex items-center gap-1.5 rounded-sm border border-[#1F3A68] px-3 py-1.5 text-sm font-semibold text-[#1F3A68] hover:bg-[#1F3A68] hover:text-white" data-testid={`add-q-${t.id}`}><Plus size={14} /> Add Question</button>
          )}
        </Panel>
      ))}
    </>
  );
}

function CourseNotesTab() {
  const [courses, setCourses] = useState([]);
  const [slug, setSlug] = useState("");
  const refresh = useCallback(() => api.get("/courses").then(({ data }) => { setCourses(data); setSlug((s) => s || (data[0] && data[0].slug) || ""); }).catch(() => {}), []);
  useEffect(() => { refresh(); }, [refresh]);
  const current = courses.find((c) => c.slug === slug);
  const imgs = current?.sample_notes_images || [];

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/courses/${slug}/sample-notes`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sample note image added");
      refresh();
    } catch { toast.error("Upload failed"); }
  };
  const remove = async (i) => {
    await api.delete(`/courses/${slug}/sample-notes/${i}`);
    toast.success("Image removed");
    refresh();
  };

  return (
    <Panel title="Manage Sample Handwritten Notes">
      <p className="mb-3 text-xs text-gray-500">Add or remove the preview note images shown on each course page. All preview images are automatically watermarked with "Sample — UPSC With Arpita".</p>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="text-sm font-semibold text-[#0B2545]">Course:</label>
        <select value={slug} onChange={(e) => setSlug(e.target.value)} className={`${inputCls} max-w-xs`} data-testid="notes-course-select">
          {courses.map((c) => <option key={c.slug} value={c.slug}>{c.title}</option>)}
        </select>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-sm bg-[#1F3A68] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B2545]" data-testid="notes-upload">
          <Upload size={15} /> Add Image
          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="notes-manager-grid">
        {imgs.map((_, i) => (
          <div key={i} className="gov-card relative overflow-hidden" data-testid={`notes-item-${i}`}>
            <img src={`${API}/courses/${slug}/sample-notes/${i}`} alt={`Sample page ${i + 1}`} className="h-36 w-full bg-white object-cover object-top" />
            <button onClick={() => remove(i)} className="absolute right-1 top-1 rounded-full bg-[#C4302B] p-1 text-white hover:bg-[#991B1B]" data-testid={`notes-del-${i}`}><Trash2 size={14} /></button>
            <span className="absolute bottom-1 left-1 rounded-sm bg-[#0B2545]/80 px-1.5 text-[10px] font-semibold text-white">{i + 1}</span>
          </div>
        ))}
        {imgs.length === 0 && <p className="col-span-full py-6 text-center text-sm text-gray-500">No sample images for this course yet. Click "Add Image" to upload.</p>}
      </div>
    </Panel>
  );
}

function EnrollmentsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/enrollments").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return (
    <Panel title={`Course Enrolment Clicks (${rows.length})`}>
      <p className="mb-3 text-xs text-gray-500">Every time a student clicks "Enrol via Razorpay", a lead is captured here (with their account details if logged in).</p>
      <div className="overflow-x-auto">
        <table className="zebra w-full border border-[#E5E7EB] text-sm" data-testid="admin-enrollments-table">
          <thead>
            <tr className="bg-[#1F3A68] text-left text-white">
              <th className="px-3 py-2">Course</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Mobile</th><th className="px-3 py-2">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#E5E7EB]">
                <td className="px-3 py-2 font-medium text-[#0B2545]">{r.course_title}</td>
                <td className="px-3 py-2">{r.name || <span className="text-gray-400">Guest</span>}</td>
                <td className="px-3 py-2">{r.email || "—"}</td>
                <td className="px-3 py-2">{r.mobile || "—"}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{new Date(r.created_at).toLocaleString("en-IN")}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500">No enrolment clicks yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function StudentsTab() {
  const [students, setStudents] = useState([]);
  useEffect(() => { api.get("/admin/students").then(({ data }) => setStudents(data)).catch(() => {}); }, []);
  return (
    <Panel title={`Registered Students (${students.length})`}>
      <div className="overflow-x-auto">
        <table className="zebra w-full border border-[#E5E7EB] text-sm" data-testid="admin-students-table">
          <thead>
            <tr className="bg-[#1F3A68] text-left text-white">
              <th className="px-3 py-2">Name</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Mobile</th><th className="px-3 py-2">Year</th><th className="px-3 py-2">State</th><th className="px-3 py-2">Source</th>
            </tr>
          </thead>
          <tbody>
            {students.map((s) => (
              <tr key={s.user_id} className="border-t border-[#E5E7EB]">
                <td className="px-3 py-2 font-medium text-[#0B2545]">{s.name}</td>
                <td className="px-3 py-2">{s.email}</td>
                <td className="px-3 py-2">{s.mobile || "—"}</td>
                <td className="px-3 py-2">{s.target_year || "—"}</td>
                <td className="px-3 py-2">{s.state || "—"}</td>
                <td className="px-3 py-2">{s.referral_source || "—"}</td>
              </tr>
            ))}
            {students.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">No students yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function MessagesTab({ onChange }) {
  const [msgs, setMsgs] = useState([]);
  const load = useCallback(() => api.get("/contact").then(({ data }) => setMsgs(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const markRead = async (id) => { await api.put(`/contact/${id}/read`); load(); onChange?.(); };
  return (
    <Panel title={`Contact Messages (${msgs.length})`}>
      <ul className="space-y-3" data-testid="admin-messages-list">
        {msgs.length === 0 && <li className="py-6 text-center text-sm text-gray-500">No messages yet.</li>}
        {msgs.map((m) => (
          <li key={m.id} className={`rounded-sm border p-3 ${m.read ? "border-[#E5E7EB] bg-white" : "border-[#FDE68A] bg-[#FEF3C7]"}`} data-testid={`message-${m.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-[#0B2545]">{m.subject}</p>
                <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500"><Mail size={12} /> {m.name} · {m.email}</p>
                <p className="mt-2 text-sm text-gray-700">{m.message}</p>
              </div>
              {!m.read && <button onClick={() => markRead(m.id)} className="flex shrink-0 items-center gap-1 rounded-sm border border-[#1F3A68] px-2 py-1 text-xs font-semibold text-[#1F3A68] hover:bg-[#1F3A68] hover:text-white" data-testid={`mark-read-${m.id}`}><CheckCheck size={13} /> Read</button>}
            </div>
            <p className="mt-2 text-[11px] text-gray-400">{new Date(m.created_at).toLocaleString("en-IN")}</p>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
